/**
 * OnboardingWizard V8 - Design System v2
 * Matching Login page aesthetic with glassmorphism & floating particles
 *
 * ✅ Simplifié: l'auth est gérée par OnboardingRoute
 * Ce composant se concentre uniquement sur l'UI d'onboarding
 */

import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Loader2, FileText } from 'lucide-react';
import { useOnboarding } from '@/hooks/useOnboarding';
import { logger } from '@/utils/logger';
import { TopHeader } from './TopHeader';
import { HorizontalStepper, StepperStep } from './HorizontalStepper';
import { Button } from '@/design-system/components';

// Import des composants des étapes
import { CompanySetupStepModern } from './CompanySetupStepModern';
import { LogoUploadStep } from './LogoUploadStep';
import { LegalMentionsStep } from './LegalMentionsStep';
import { FirstClientStep } from './FirstClientStep';
import { FirstProductStep } from './FirstProductStep';
import { FirstQuoteStep } from './FirstQuoteStep';
import { MigrateInvoicesStep } from './MigrateInvoicesStep';
import { CompletionStep } from './CompletionStep';

// Floating particles animation component - matches Login v2
const FloatingParticles = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[...Array(8)].map((_, i) => (
      <div
        key={i}
        className="absolute w-2 h-2 bg-lime-400/30 rounded-full animate-float"
        style={{
          left: `${10 + i * 12}%`,
          top: `${15 + (i % 4) * 20}%`,
          animationDelay: `${i * 0.6}s`,
          animationDuration: `${4 + i * 0.5}s`
        }}
      />
    ))}
    {[...Array(5)].map((_, i) => (
      <div
        key={`secondary-${i}`}
        className="absolute w-1.5 h-1.5 bg-white/20 rounded-full animate-float-slow"
        style={{
          right: `${8 + i * 18}%`,
          bottom: `${20 + (i % 3) * 25}%`,
          animationDelay: `${i * 0.8}s`,
          animationDuration: `${5 + i * 0.7}s`
        }}
      />
    ))}
  </div>
);

export const OnboardingWizard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    status,
    isLoading,
    error,
    isCompleted,
    nextStepName,
    refresh,
    nextStep,
  } = useOnboarding();


  // Redirection automatique quand l'onboarding est complété
  useEffect(() => {
    if (isCompleted) {
      logger.log('🎯 OnboardingWizard: Completed, redirecting to dashboard...');
      navigate('/dashboard');
    }
  }, [isCompleted, navigate]);

  // Si complété, afficher un message de chargement pendant la redirection
  if (isCompleted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800 flex items-center justify-center relative overflow-hidden">
        <FloatingParticles />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(212,255,0,0.15),transparent_50%)]" />
        <div className="text-center relative z-10">
          <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-lime-400 to-lime-500 flex items-center justify-center shadow-lg shadow-lime-500/30 animate-pulse">
            <FileText size={32} className="text-teal-700" />
          </div>
          <Loader2 className="w-8 h-8 text-lime-400 animate-spin mx-auto mb-4" />
          <p className="text-white font-medium text-lg">{t('onboarding.wizard.redirecting')}</p>
          <p className="text-white/60 text-sm mt-2">{t('onboarding.wizard.configComplete')}</p>
        </div>
      </div>
    );
  }

  // Loading initial
  if (isLoading && !status) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800 flex items-center justify-center relative overflow-hidden">
        <FloatingParticles />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(212,255,0,0.15),transparent_50%)]" />
        <div className="text-center relative z-10">
          <Loader2 className="w-12 h-12 text-lime-400 animate-spin mx-auto mb-4" />
          <p className="text-white font-medium">{t('onboarding.wizard.loadingProgress')}</p>
        </div>
      </div>
    );
  }

  // Erreur de chargement
  if (!status) {
    logger.error('OnboardingWizard: impossible de charger le statut', { error, isLoading });

    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800 flex items-center justify-center relative overflow-hidden">
        <FloatingParticles />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(212,255,0,0.15),transparent_50%)]" />
        <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 max-w-md shadow-2xl border border-white/20 relative z-10">
          <p className="text-white mb-4 font-medium">
            {error || t('onboarding.wizard.loadError')}
          </p>
          <Button
            onClick={() => window.location.reload()}
            className="w-full bg-lime-500 hover:bg-lime-400 text-teal-800 font-semibold"
          >
            {t('onboarding.wizard.retry')}
          </Button>
        </div>
      </div>
    );
  }

  const { progress, stepsCompleted, totalSteps } = status;

  logger.log('🎯 OnboardingWizard: SHOWING FORM', { nextStepName, stepsCompleted, totalSteps });

  // Liste des étapes pour le stepper horizontal
  const steps: StepperStep[] = [
    {
      id: 'company',
      label: t('onboarding.wizard.stepCompany'),
      number: 1,
      status: progress?.companyCreated
        ? 'completed'
        : nextStepName === 'company'
        ? 'current'
        : 'upcoming',
    },
    {
      id: 'legal',
      label: t('onboarding.wizard.stepLegal'),
      number: 2,
      status: progress?.legalMentionsAdded
        ? 'completed'
        : nextStepName === 'legal'
        ? 'current'
        : 'upcoming',
    },
    {
      id: 'logo',
      label: t('onboarding.wizard.stepLogo'),
      number: 3,
      status: progress?.logoUploaded
        ? 'completed'
        : nextStepName === 'logo'
        ? 'current'
        : 'upcoming',
    },
    {
      id: 'client',
      label: t('onboarding.wizard.stepClient'),
      number: 4,
      status: progress?.firstClientAdded
        ? 'completed'
        : nextStepName === 'client'
        ? 'current'
        : 'upcoming',
    },
    {
      id: 'product',
      label: t('onboarding.wizard.stepProduct'),
      number: 5,
      status: progress?.firstProductAdded
        ? 'completed'
        : nextStepName === 'product'
        ? 'current'
        : 'upcoming',
    },
    {
      id: 'quote',
      label: t('onboarding.wizard.stepQuote'),
      number: 6,
      status: progress?.firstQuoteCreated
        ? 'completed'
        : nextStepName === 'quote'
        ? 'current'
        : 'upcoming',
    },
    {
      id: 'migrate',
      label: t('onboarding.wizard.stepMigrate', 'Migration'),
      number: 7,
      status: nextStepName === 'migrate'
        ? 'current'
        : nextStepName === null
        ? 'completed'
        : 'upcoming',
    },
  ];

  // Déterminer l'étape courante à afficher
  const getCurrentStepComponent = () => {
    if (!nextStepName) {
      return <CompletionStep onComplete={refresh} />;
    }

    switch (nextStepName) {
      case 'company':
        return <CompanySetupStepModern onComplete={refresh} />;
      case 'legal':
        return <LegalMentionsStep onComplete={refresh} onSkip={nextStep} />;
      case 'logo':
        return <LogoUploadStep onComplete={refresh} onSkip={nextStep} />;
      case 'client':
        return <FirstClientStep onComplete={refresh} onSkip={nextStep} />;
      case 'product':
        return <FirstProductStep onComplete={refresh} onSkip={nextStep} />;
      case 'quote':
        return <FirstQuoteStep onComplete={refresh} onSkip={nextStep} />;
      case 'migrate':
        return <MigrateInvoicesStep onComplete={refresh} onSkip={nextStep} />;
      default:
        return <CompletionStep onComplete={refresh} />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800 overflow-x-hidden relative">
      {/* Background Effects - Matching Login v2 */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(212,255,0,0.15),transparent_50%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(212,255,0,0.1),transparent_50%)]" />
      <div className="absolute inset-0 opacity-[0.03] bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')]" />

      <FloatingParticles />

      <div className="relative z-10">
        {/* Top Header */}
        <TopHeader onHelpClick={() => window.open('https://help.facturxpro.com', '_blank')} />

        {/* Main Content */}
        <main className="mx-auto max-w-7xl px-6 py-8 animate-fade-in-up">
          {/* Horizontal Stepper - Glass Card */}
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 mb-8 shadow-2xl border border-white/20">
            <HorizontalStepper
              steps={steps}
              currentStep={stepsCompleted + 1}
              totalSteps={totalSteps}
            />
          </div>

          {/* Error message */}
          {error && (
            <div className="mb-6 p-4 bg-red-500/20 backdrop-blur-md border border-red-400/30 rounded-2xl text-white text-sm flex items-center gap-3">
              <div className="w-6 h-6 rounded-full bg-red-500/30 flex items-center justify-center flex-shrink-0">
                <span className="text-white text-xs font-bold">!</span>
              </div>
              {error}
            </div>
          )}

          {/* Loading state */}
          {isLoading ? (
            <div className="flex items-center justify-center py-24">
              <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-12 border border-white/20">
                <Loader2 className="w-10 h-10 text-lime-400 animate-spin mx-auto" />
                <p className="text-white/80 mt-4 font-medium">{t('onboarding.wizard.loading')}</p>
              </div>
            </div>
          ) : (
            /* Step content */
            <div className="animate-scale-in">
              {getCurrentStepComponent()}
            </div>
          )}
        </main>
      </div>

      {/* Custom Animations - Matching Login v2 */}
      <style>{`
        @keyframes slide-in {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0) scale(1);
            opacity: 0.3;
          }
          50% {
            transform: translateY(-20px) scale(1.2);
            opacity: 0.6;
          }
        }

        @keyframes float-slow {
          0%, 100% {
            transform: translateY(0) scale(1);
            opacity: 0.2;
          }
          50% {
            transform: translateY(-30px) scale(1.1);
            opacity: 0.4;
          }
        }

        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes scale-in {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .animate-slide-in {
          animation: slide-in 0.6s ease-out;
        }

        .animate-float {
          animation: float 4s ease-in-out infinite;
        }

        .animate-float-slow {
          animation: float-slow 6s ease-in-out infinite;
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.6s ease-out forwards;
        }

        .animate-scale-in {
          animation: scale-in 0.4s ease-out forwards;
        }
      `}</style>
    </div>
  );
};
